<script>
    import Preview from "./Preview.svelte";
    import { hostname, isAuthenticated, stack } from "../stores.js";
    import { selectedPreview, finalFdkSettings, canvasSize, zoom, lastBeamhardCuppingPreviewImageIndex, newlyRequestedBeamhardCuppingPreviewSlices, lastCentershiftPreviewImageIndex, USE_IMAGE_SMOOTHING } from "../previewstores.js";
    import { formatJson, isNumeric, now, runPreviewApi } from "../utils.js";
    import RangeSlider from "svelte-range-slider-pips";
    import BeamhardGraph from "./BeamhardGraph.svelte";
    import { onMount, onDestroy } from "svelte";
    import Modal from "./Modal.svelte";
    import { toast } from "@zerodevx/svelte-toast";
    import { toastThemeError, toastThemeInfo } from "../toasts.js";
    import { pipelineObserver } from '../pipelinestores.js';
    import { tooltip } from "svooltip";
    import 'svooltip/styles.css';

    export let params;
    export let sliceIndex;
    export let centershiftSlices;
    export let beamhardCuppingSlicePreviews;
    export let allBeamhardCuppingSlicePreviews;

    let beamhardCuppingPreviewImageIndex = isNumeric($lastBeamhardCuppingPreviewImageIndex) ? $lastBeamhardCuppingPreviewImageIndex : 0;
    let canvasWidth = $canvasSize;
    let beamhardCrossY = canvasWidth / 2;
    let sourceCanvas;
    let previewManipulationCanvas;
    let previewOverlayCanvas;
    let rotationCanvas;
    let showModal = false;

    $: initPreview($selectedPreview);
    $: onCanvasSizeChanged($canvasSize);
    $: onPreviewOverlayElementLoaded(previewOverlayCanvas); // TODO: Explain what this does...
    $: onAngleOffsetChanged($finalFdkSettings?.angle_offset);
    $: onPreviewManipulationElementLoaded(previewManipulationCanvas);

    function onCanvasSizeChanged(updatedCanvasSize) {
        canvasWidth = updatedCanvasSize;
        beamhardCrossY = canvasWidth / 2;
        initRotationCanvas();
    }

    function extractPhaseRetrievalBase(datasetPath) {
        let pattern = /.*\/(.*-ph\d+)-.*/;
        let phaseRetrieval = datasetPath.match(pattern);
        console.log("BEAMHARD_CUPPING USING PHASE RETRIEVAL BASE: ", phaseRetrieval);
        if (phaseRetrieval.length > 0) {
            return phaseRetrieval[1];
        }
    }

    function updateFdkSettings(options) {
        $finalFdkSettings = {
            ...$finalFdkSettings,
            ...options,
        };

        console.log("FINAL FDK SETTINGS ARE NOW: ", $finalFdkSettings);
    }

    function onChangedBeamhardCuppingValue(previewImageIndex) {
        let beamhardCuppingVal =
            $selectedPreview?.preview_output?.[0]?.parameter_values?.[previewImageIndex].toFixed(2);
        let phaseRetrievalBase = extractPhaseRetrievalBase($selectedPreview?.preview_output?.[0]?.large_image_pattern);
        let changedSettings = {
            beamhard_cupping: beamhardCuppingVal / 1.0,
            phase_retrieval_dataset_id: phaseRetrievalBase,
        };
        console.log("BEAMHARD_CUPPING CHANGED FDK SETTINGS TO: ", changedSettings);
        updateFdkSettings(changedSettings);
    }

    // If the angle is updated, trigger beamhard graph update also
    function onAngleOffsetChanged(updatedAngle) {
        console.log('******* CALLED BHC_PREVIEW ONANGLEOFFSETCHANGED:', updatedAngle, 'degrees');
        previewManipulationCanvas = previewManipulationCanvas;
    }

    function onClickPreviewImage(ev) {
        let x = ev.offsetX;
        let y = ev.offsetY;
        if (y >= 0 && y < canvasWidth) {
            beamhardCrossY = y;
        }
        console.log(`X=${x} Y=${y}`);
        // Previously did updateCrossData(img);. Now forcing BeamhardGraph component to update upon sourceCanvas assignment
        sourceCanvas = sourceCanvas;
        previewManipulationCanvas = previewManipulationCanvas; // Force BeamhardGraph to update on click
        clearCrossLines();
        drawCrossLine(y, "blue");
    }

    let octx;
    async function onPreviewImageMouseMove(ev) {
        // Make sure the drawing context is initialised
        if (!previewOverlayCanvas) {
            previewOverlayCanvas = document.getElementById("previewOverlayCanvas");
            octx = previewOverlayCanvas.getContext("2d");
        }

        // Clear overlay canvas and draw moving line over image as well as existing selected line
        if (octx) {
            clearCrossLines();
            drawCrossLine(beamhardCrossY, "blue");
            drawCrossLine(ev.offsetY, "yellow");
        }
    }

    async function onPreviewImageMouseOut() {
        // Make sure the drawing context is initialised
        if (!previewOverlayCanvas) {
            previewOverlayCanvas = document.getElementById("previewOverlayCanvas");
            octx = previewOverlayCanvas.getContext("2d");
        }

        // Clear overlay canvas and draw existing selected line
        if (octx) {
            clearCrossLines();
            drawCrossLine(beamhardCrossY, "blue");
        }
    }
    async function clearCrossLines() {
        if (!previewOverlayCanvas) {
            previewOverlayCanvas = document.getElementById("previewOverlayCanvas");
            octx = previewOverlayCanvas.getContext("2d");
        }
        if (octx) {
            octx.clearRect(0, 0, previewOverlayCanvas.width, previewOverlayCanvas.height);
        }
    }

    async function drawCrossLine(y, color) {
        // Make sure the drawing context is initialised
        if (!previewOverlayCanvas) {
            previewOverlayCanvas = document.getElementById("previewOverlayCanvas");
            octx = previewOverlayCanvas.getContext("2d");
        }
        // Draws a sticky line
        if (octx) {
            if (y >= 0 && y < canvasWidth) {
                octx.beginPath();
                octx.moveTo(1, y);
                octx.lineTo(canvasWidth, y);
                octx.strokeStyle = color;
                octx.stroke();
            }
        }
    }

    // Helper function to filter away all beamhard cupping values that don't match the current
    // phase retrieval settings, then returning a list of the cenetershift values used by the 
    // remaining beamhard cupping previews.
    function getAllAvailableBeamhardCuppingPreviewCsValues() {
        const phFilteredBhcSlicePreviews = allBeamhardCuppingSlicePreviews?.filter((p) => {
            const delta = p?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta;
            const mu = p?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu;
            const deltaByMu = delta / mu;
            const deltaFromSettings = $finalFdkSettings?.delta;
            const muFromSettings = $finalFdkSettings?.mu;
            const deltaByMuFromSettings = deltaFromSettings / muFromSettings;
            const epsilon = 1e-10;
            const matchesPhaseRetrieval = Math.abs(deltaByMu - deltaByMuFromSettings) < epsilon;
            if (matchesPhaseRetrieval) return true;
            return false;
        });
        const csValues = phFilteredBhcSlicePreviews?.map((p) => {
            return p?.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_object_mm;
        });
        return csValues;
    }

    // Helper function to generate a short string key that can be used for bookkeeping of previews being requested
    function getKeyFromPreview(preview) {
        if (preview) {
            const path = preview.preview_output?.[0]?.large_image_pattern;
            const phBase = extractPhaseRetrievalBase(path);
            const cs = preview.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_object_mm;
            const key = `${phBase}|${cs}`;
            return key;
        } else {
            const phBase = $finalFdkSettings?.phase_retrieval_dataset_id;
            const cs = $finalFdkSettings?.centershift_object_mm; 
            const key = `${phBase}|${cs}`;
            return key;
        }
    }

    // This should be called whenever the selectedPreview changes
    function initPreview($selectedPreview) {
        // Remove and actually existing previews from the newly requested set (as new data may have become available)
        beamhardCuppingSlicePreviews?.map(p => {
            const key = getKeyFromPreview(p);
            if (!key) return;
            $newlyRequestedBeamhardCuppingPreviewSlices.delete(key);
            $newlyRequestedBeamhardCuppingPreviewSlices = $newlyRequestedBeamhardCuppingPreviewSlices;
        });
        if ($selectedPreview) {
            console.log("++++++++ INIT BEAMHARDCUPPINGPREVIEW -- ", $selectedPreview);
            previewOverlayCanvas?.addEventListener("click", onClickPreviewImage);

            // If we have a beamhard cupping value, set the corresponding image index
            if (isNumeric($finalFdkSettings?.beamhard_cupping)) {
                beamhardCuppingPreviewImageIndex = Math.round($finalFdkSettings?.beamhard_cupping * 63);
                console.log('BEAMHARD_CUPPING PREVIEW FOUND IMAGE INDEX: ', $finalFdkSettings?.beamhard_cupping, ' -> ', beamhardCuppingPreviewImageIndex);
            } else {
                console.log('BEAMHARD_CUPPING SET TO DEFAULT 0');
                $finalFdkSettings.beamhard_cupping = 0; // If not user-defined, default to 0
            }
        } else {
            // If we have no beamhard cupping preview, offer to generate
            console.log('BEAMHARD_CUPPING SET TO DEFAULT 0');
            $finalFdkSettings.beamhard_cupping = 0; // If not user-defined, default to 0
        }

        $lastBeamhardCuppingPreviewImageIndex = beamhardCuppingPreviewImageIndex;
    }

    let mctx;
    async function onPreviewManipulationElementLoaded(previewManipulationCanvas) {
        console.log("******* CALLED ONPREVIEWMANIPULATIONELEMENTLOADED!");
        if (!previewManipulationCanvas) {
            return;
        }

        // Make sure the drawing context is initialised
        if (!mctx) {
            mctx = previewManipulationCanvas.getContext("2d");
            mctx.imageSmoothingEnabled = $USE_IMAGE_SMOOTHING;
        }

        // Copy the rotated image onto rotationCanvas
        if (rotationCanvas && rctx && mctx) {
            console.log("**** COPYING ONTO ROTATION CANVAS");
            rotationCanvas.width = canvasWidth * $zoom;
            rotationCanvas.height = canvasWidth * $zoom;

            // Read the image data from the manipulation canvas
            // and then create a bitmap out of it.
            let imgData = mctx.getImageData(0, 0, canvasWidth * $zoom, canvasWidth * $zoom);
            const bitmap = await createImageBitmap(imgData);
            // Temporarily rotate the rotation canvas from its centre
            // and draw the bitmap onto it.
            rctx.save();
            rctx.translate((canvasWidth * $zoom) / 2, (canvasWidth * $zoom) / 2);
            rctx.rotate((-$finalFdkSettings.angle_offset * 2 * Math.PI) / 360);
            rctx.translate((-canvasWidth * $zoom) / 2, (-canvasWidth * $zoom) / 2);
            rctx.imageSmoothingEnabled = $USE_IMAGE_SMOOTHING;
            rctx.drawImage(bitmap, 0, 0);
            //DEBUG: rctx.fillRect(canvasWidth / 2 - 50, canvasWidth / 2 - 25, 100, 50);
            rctx.restore();

            rotationCanvas = rotationCanvas;
        }
    }

    function onPreviewOverlayElementLoaded(previewOverlayCanvas) {
        if (previewOverlayCanvas && previewOverlayCanvas.getAttribute("listener")) {
            return;
        } else {
            previewOverlayCanvas?.addEventListener("click", onClickPreviewImage);
            previewOverlayCanvas?.addEventListener("mousemove", onPreviewImageMouseMove);
            previewOverlayCanvas?.addEventListener("mouseout", onPreviewImageMouseOut);
            octx = previewOverlayCanvas?.getContext("2d");

            if (previewOverlayCanvas) {
                clearCrossLines();
                drawCrossLine(beamhardCrossY, "blue");
            }
        }
    }

    onDestroy(() => {
        previewOverlayCanvas?.removeEventListener("click", onClickPreviewImage);
        previewOverlayCanvas?.removeEventListener("click", onPreviewImageMouseMove);
        previewOverlayCanvas?.removeEventListener("click", onPreviewImageMouseOut);
    });

    let rctx;
    onMount(() => {
        initRotationCanvas();
    });

    function initRotationCanvas() {
        rotationCanvas = document.createElement("canvas");
        //DEBUG: rotationCanvas = document.getElementById('rotationCanvas');
        rctx = rotationCanvas.getContext("2d");
        rctx.imageSmoothingEnabled = $USE_IMAGE_SMOOTHING;
    }

    let beamhardCuppingPreviewSettings;
    function startBeamhardCuppingPreview() {
        if ($selectedPreview) {
            beamhardCuppingPreviewSettings = { ...$selectedPreview.preview_input };
        } else {
            beamhardCuppingPreviewSettings = { 
                generic_preview_input: {
                    preprocessing_settings: {},
                    phase_retrieval_settings: {
                        phase_retrieval: {
                            delta: $finalFdkSettings?.delta,
                            mu: $finalFdkSettings?.mu
                        }
                    },
                    reconstruction_settings: { 
                        centershift_object_mm: $finalFdkSettings?.centershift_object_mm,
                        tilt_deg: $finalFdkSettings?.tilt_deg
                    }
                },
                previews: []
            };
        }
        beamhardCuppingPreviewSettings.previews?.push(previewBeamhardCupping); // Ask for beamhard cupping preview

        // Slices for which to generate previews
        let newPreviewSlicesToGenerate = [...new Set(centershiftSlices)]; // Deduplicate centershift slices
        beamhardCuppingPreviewSettings.generic_preview_input.slices = newPreviewSlicesToGenerate;

        showModal = true;
    }

    const previewBeamhardCupping = {
        preview_type: "BEAMHARD_CUPPING",
    };

    async function onConfirmBeamhardCuppingPreviewSettings() {
        console.log("OK, CALLING PREVIEW PIPELINE FOR CENTERSHIFT...", params?.session);
        const previewInput = beamhardCuppingPreviewSettings;
        const runPreviewApiResponse = await runPreviewApi(
            $hostname,
            $isAuthenticated,
            $stack,
            params.session,
            params.tomography,
            previewInput
        );

        if (isNumeric(runPreviewApiResponse)) {
            let msg = `${now()}<br/>Run preview API error: ${runPreviewApiResponse}`;
            toast.push(msg, toastThemeError);
            return;
        }

        // The API should respond with a fully qualified pipeline name
        let pipelineId = runPreviewApiResponse?.id;

        // Make a note of the preview we requested to add, so that we have a chance 
        // to indicate to the user that it is being processed and avoid that the user
        // request the same preview again
        $newlyRequestedBeamhardCuppingPreviewSlices.add(getKeyFromPreview());
        $newlyRequestedBeamhardCuppingPreviewSlices = $newlyRequestedBeamhardCuppingPreviewSlices;

        // Indicate to the user that a new preview job has been started
        let msg = `${now()}<br/>`;
        msg += `Started beamhard cupping preview at slice ${beamhardCuppingPreviewSettings?.generic_preview_input?.slices}
            with centershift=${beamhardCuppingPreviewSettings.generic_preview_input.reconstruction_settings.centershift_object_mm}
            and phase retrieval &delta;=${beamhardCuppingPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.delta}
            and &mu;=${beamhardCuppingPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.mu}<br/>
            for ${params.session}/${params.tomography}`;
        if (pipelineId) {
            msg += ` <br/>(${pipelineId})`;
        }
        toast.push(msg, toastThemeInfo);

        // Add the new pipeline to the pipeline observables list
        $pipelineObserver.addPipelineToObserve(pipelineId);
    }

    let showJson = false;
    function onShowJson() {
        showJson = true;
    }

    function goToCs(centershift) {
        console.log('GOING TO CS=', centershift);
        $lastCentershiftPreviewImageIndex = undefined;
        updateFdkSettings({centershift_object_mm: centershift});
    }
</script>

<Preview
    headerText="Beamhard cupping preview"
    type="BEAMHARD_CUPPING"
    {sliceIndex}
    {centershiftSlices}
    availableSlicePreviews={beamhardCuppingSlicePreviews}
    previewImageIndex={beamhardCuppingPreviewImageIndex}
    bind:sourceCanvas
    bind:previewManipulationCanvas
    bind:previewOverlayCanvas
    on:carousel>
    {#if $selectedPreview}
        <RangeSlider
            id="previewsliderBeamhardCupping"
            values={[beamhardCuppingPreviewImageIndex]}
            max={63}
            pips
            float
            first="label"
            last="label"
            formatter={(v) => $selectedPreview?.preview_output?.[0]?.parameter_values?.[v].toFixed(2)}
            springValues={{stiffness: 1, damping:1}}
            on:change={(e) => {
                console.log("SLIDER=", e.detail.value);
                beamhardCuppingPreviewImageIndex = e.detail.value;
                $lastBeamhardCuppingPreviewImageIndex = beamhardCuppingPreviewImageIndex;
                onChangedBeamhardCuppingValue(beamhardCuppingPreviewImageIndex);
            }} />
        {#if $selectedPreview?.preview_output?.find((s) => s.slice === sliceIndex)}
            <BeamhardGraph canvas={rotationCanvas} {beamhardCrossY} />
        {/if}
        <!--DEBUG: canvas id="rotationCanvas" width={canvasWidth} height={canvasWidth}></canvas-->
    {:else}
        {#if !$newlyRequestedBeamhardCuppingPreviewSlices.has(getKeyFromPreview())}
            <button style="display: flex; margin-left: auto; margin-right: auto;"
                on:click|preventDefault|stopPropagation={() => {
                    startBeamhardCuppingPreview();
                }}>
                Generate beamhard cupping preview...
            </button>

            <annotation class="generateAnnotation" style="--canvasWidth:{canvasWidth}px">
                <p>
                    No beamhard cupping preview exists for this phase retrieval &delta;={$finalFdkSettings?.delta?.toExponential()} &mu;={$finalFdkSettings?.mu?.toPrecision(3)} or centershift={($finalFdkSettings?.centershift_object_mm * 1000)?.toFixed(3)} μm. Press the button above to start generating one for each slice.
                    If you are happy to proceed without adjusting beamhard cupping, you can skip this step and then beamhard cupping factor = 0 will be used.
                </p>
            </annotation>
        {:else}
            <p>Preview &delta;={$finalFdkSettings?.delta?.toExponential()} &mu;={$finalFdkSettings?.mu?.toPrecision(3)} centershift={($finalFdkSettings?.centershift_object_mm * 1000)?.toFixed(3)} μm is currently processing. Please wait...</p>
        {/if}
        <annotation class="generateAnnotation" style="--canvasWidth:{canvasWidth}px">
            {#if getAllAvailableBeamhardCuppingPreviewCsValues()?.length > 0}
                <p>
                    Beamhard cupping previews already exist for centershift =
                    {#each getAllAvailableBeamhardCuppingPreviewCsValues() as cs, i}
                        {i > 0 ? ', ' : ' '}
                        <!-- svelte-ignore a11y-invalid-attribute -->
                        <a href="" on:click|preventDefault|stopPropagation={() => goToCs(cs)} use:tooltip={{content: `Switch to centershift ${cs} mm`, placement: "top"}}>
                            {(cs * 1000)?.toFixed(3)} μm
                        </a>
                    {/each}
                    <br/>
                    Click to apply selected value as your current centershift setting!
                </p>
            {/if}
        </annotation>
    {/if}
    {#if showModal}
        <Modal
            on:close={() => {
                showJson = false;
                showModal = false;
            }}
            on:ok={() => {
                showModal = false;
                onConfirmBeamhardCuppingPreviewSettings();
                showJson = false;
            }}
            on:json={onShowJson}
            jsonable={true}>
            <h2 slot="header">Confirm beamhard cupping preview settings</h2>
            {#if showJson}
                <pre>{formatJson(beamhardCuppingPreviewSettings)}</pre>
            {:else}
                <h3>Preprocessing settings</h3>
                <settings>
                    delta: {beamhardCuppingPreviewSettings?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta?.toExponential()}
                    <br />
                    mu: {beamhardCuppingPreviewSettings?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu?.toPrecision(
                        3
                    )}
                    <br />
                </settings>
                <h3>Reconstruction settings</h3>
                <settings>
                    centershift: {beamhardCuppingPreviewSettings?.generic_preview_input?.reconstruction_settings?.centershift_object_mm} mm
                    <br />
                    {#if isNumeric(beamhardCuppingPreviewSettings?.generic_preview_input?.reconstruction_settings?.tilt_deg)}
                        tilt: {beamhardCuppingPreviewSettings?.generic_preview_input?.reconstruction_settings?.tilt_deg?.toPrecision(
                            2
                        )}°
                    <br />
                    {/if}
                    slices: {beamhardCuppingPreviewSettings?.generic_preview_input?.slices}
                </settings>
            {/if}
        </Modal>
    {/if}
</Preview>

<style>
    :global(#previewsliderBeamhardCupping) {
        width: 100%;
        margin: 0;
        margin-left: auto;
        margin-right: auto;
    }
    :global(#previewsliderBeamhardCupping .pip.last, #previewsliderBeamhardCupping .pip.first) {
        height: 0.75em;
    }
    :global(#previewsliderBeamhardCupping .rangePips .pip.first .pipVal) {
        padding-left: 1em;
    }
    :global(#previewsliderBeamhardCupping .rangePips .pip.last .pipVal) {
        padding-right: 1em;
    }

    :global(.generateAnnotation) {
        display: block;
        width: var(--canvasWidth);
    }

    settings {
        font-family: monospace;
    }
</style>
